<template>
	<main class="app-main">
		<div class="content-wrap">
			<section class="content-section">
				<header class="section-header">
					<h2 class="section-title">{{ $t('routes.investments.title') }}</h2>
				</header>
				<article>
					<chart-investment-info-group :investment-info-data="investmentsInfoData" :charts-data="chartsData" :tabs="true" v-if="investmentsData && investmentsInfoData.length"/>
					<p v-else>{{ $t('investments.no_investment_products') }}</p>
				</article>
			</section>
		</div>
	</main>
</template>

<script>
import InvestmentsOptionsForm from "@/components/forms/InvestmentsOptionsForm";
import ChartInvestmentInfoGroup from "../components/containers/ChartInvestmentInfoGroup";
import investmentsMixin from "../mixins/investmentsMixin";

export default {
	name: "InvestmentsHome",
	components: {
		InvestmentsOptionsForm,
		ChartInvestmentInfoGroup
	},
	mixins: [investmentsMixin],
	data() {
		return {}
	},
	computed: {
		chartsData() {
			let data = [
				{
					tabName: this.$t('investments.allocation'),
					chartOptions: {
						chart: {
							type: 'donut',
						},
						legend: {
							show: true,
							position: 'right',
							fontSize: '16px',
							fontFamily: 'Rubik,sans-serif',
							labels: {
								colors: 'inherit',
							},
							markers: {
								width: 15,
								height: 15,
								radius: 3,
								offsetX: -5,
								offsetY: 2
							},
						},
						colors: [
							'#f9a03a',
							'#2BA748',
							'#008FFB',
							'#775DD0',
							'#FF4560',
							'#13D8AA',
						],
						plotOptions: {
							pie: {
								borderWidth: 0
							}
						},
						stroke: {
							show: true,
							curve: 'smooth',
							lineCap: 'butt',
							colors: [
								'#f9a03a',
								'#2BA748',
								'#008FFB',
								'#775DD0',
								'#FF4560',
								'#13D8AA',
							],
							width: 2,
							dashArray: 1,
						},
						tooltip: {
							enabled: true,
							offset: 100,
							y: {
								show: true,
								formatter: (seriesName) => {
									return `${seriesName} ${process.env.VUE_APP_CURRENCY}`;
								},
							},
						},
						dataLabels: {
							enabled: true,
							dropShadow: {
								enabled: true,
								top: 2,
								left: 1,
								blur: 2,
								color: '#000',
								opacity: 0.2,
							},
						},
						responsive: [{
							breakpoint: 991,
							options: {
								legend: {
									position: 'bottom',
									markers: {
										offsetX: -2,
										offsetY: 0
									}
								}
							},
						}]
					},
					totalBalance: this.investmentsData.total,
				},
				this.balanceChart,
			];

			const companyColors = {
				EIC: '#f9a03a',
				IAD: '#2BA748',
				Conseq: '#008FFB',
				Investika: '#775DD0',
				Koop: '#FF4560',
				Sympatia: '#13D8AA',
				Gutmann: '#A5978B',
			};

			let labels = [];
			let series = [];
			let colors = [];
			if (this.checkPermission('EIC') && this.investmentsData.eic.accounts.length){
				labels.push('EIC');
				series.push(this.investmentsData.eic.total);
				colors.push(companyColors.EIC);
			}
			if (this.checkPermission('IAD') && this.investmentsData.iad.accounts.length){
				labels.push('IAD');
				series.push(this.investmentsData.iad.total);
				colors.push(companyColors.IAD);
			}
			if (this.checkPermission('Conseq') && this.investmentsData.conseq.accounts.length){
				labels.push('Conseq');
				series.push(this.investmentsData.conseq.total);
				colors.push(companyColors.Conseq);
			}
			if (this.checkPermission('Investika') && this.investmentsData.investika.accounts.length){
				labels.push('Investika');
				series.push(this.investmentsData.investika.total);
				colors.push(companyColors.Investika);
			}
			if (this.checkPermission('Koop') && this.investmentsData.koop.accounts.length){
				labels.push('Kooperativa');
				series.push(this.investmentsData.koop.total);
				colors.push(companyColors.Koop);
			}
			if (this.checkPermission('Gutmann') && this.investmentsData.gutmann.accounts.length){
				labels.push('Bank Gutmann');
				series.push(this.investmentsData.gutmann.total);
				colors.push(companyColors.Gutmann);
			}
			if (this.checkPermission('Sympatia') && this.investmentsData.sympatia.accounts.length){
				labels.push('Sympatia');
				series.push(this.investmentsData.sympatia.total);
				colors.push(companyColors.Sympatia);
			}

			data[0].chartOptions.labels = labels;
			data[0].series = series;
			data[0].chartOptions.colors = colors;
			data[0].chartOptions.stroke.colors = colors;

			return data;
		},
		balanceChart(){
			let data = this.getBalanceChartOptions();

			data.tabName = this.$t('investments.profit');

			let series = [
				{
					name: this.$t('investments.account_balance'),
					data: [],
				},
				{
					name: this.$t('investments.transactions'),
					data: [],
				}
			];

			this.investmentsTimelineData?.globalBalance.forEach(item => {
				series[0].data.push({
					x: item.date,
					y: item.amount,
				});
			});

			this.investmentsTimelineData?.globalTransactions.forEach(item => {
				series[1].data.push({
					x: item.date,
					y: item.amount,
				});
			});

			data.series = series;
			data.action = 'getInvestmentsTimelineData';

			return data;
		},
		investmentsData() {
			return this.$store.state.views.investments;
		},
		investmentsTimelineData() {
			return this.$store.state.views.investmentsTimeline;
		},
		investmentsInfoData(){
			let boxes = [];

			if (this.checkPermission('EIC') && this.investmentsData.eic.accounts.length){
				boxes.push({
					name: `EIC`,
					total: this.investmentsData.eic.total.toFixed(2),
					deposits: this.investmentsData.eic.deposits.toFixed(2),
					withdrawals: this.investmentsData.eic.withdrawals.toFixed(2),
					profit: this.investmentsData.eic.profit.toFixed(2),
					profitPercentage: this.investmentsData.eic.profit_percentage.toFixed(2),
					fees: this.investmentsData.eic.fees.toFixed(2),
					hideStats: this.investmentsData.eic.hide_stats,
					detailUrl: this.$t('routes.investments_eic.path'),
					redirectDetail: this.investmentsData.eic.accounts.length,
				});
			}

			if (this.checkPermission('IAD') && this.investmentsData.iad.accounts.length){
				boxes.push({
					name: `IAD`,
					total: this.investmentsData.iad.total.toFixed(2),
					deposits: this.investmentsData.iad.deposits.toFixed(2),
					withdrawals: this.investmentsData.iad.withdrawals.toFixed(2),
					profit: this.investmentsData.iad.profit.toFixed(2),
					profitPercentage: this.investmentsData.iad.profit_percentage.toFixed(2),
					fees: this.investmentsData.iad.fees.toFixed(2),
					hideStats: this.investmentsData.iad.hide_stats,
					detailUrl: this.$t('routes.investments_iad.path'),
					redirectDetail: this.investmentsData.iad.accounts.length,
				});
			}

			if (this.checkPermission('Conseq') && this.investmentsData.conseq.accounts.length){
				boxes.push({
					name: `Conseq`,
					total: this.investmentsData.conseq.total.toFixed(2),
					deposits: this.investmentsData.conseq.deposits.toFixed(2),
					withdrawals: this.investmentsData.conseq.withdrawals.toFixed(2),
					profit: this.investmentsData.conseq.profit.toFixed(2),
					profitPercentage: this.investmentsData.conseq.profit_percentage.toFixed(2),
					fees: this.investmentsData.conseq.fees.toFixed(2),
					hideStats: this.investmentsData.conseq.hide_stats,
					detailUrl: this.$t('routes.investments_conseq.path'),
					redirectDetail: this.investmentsData.conseq.accounts.length,
				});
			}

			if (this.checkPermission('Investika') && this.investmentsData.investika.accounts.length){
				boxes.push({
					name: `Investika`,
					total: this.investmentsData.investika.total.toFixed(2),
					deposits: this.investmentsData.investika.deposits.toFixed(2),
					withdrawals: this.investmentsData.investika.withdrawals.toFixed(2),
					profit: this.investmentsData.investika.profit.toFixed(2),
					profitPercentage: this.investmentsData.investika.profit_percentage.toFixed(2),
					fees: this.investmentsData.investika.fees.toFixed(2),
					hideStats: this.investmentsData.investika.hide_stats,
					detailUrl: this.$t('routes.investments_investika.path'),
					redirectDetail: this.investmentsData.investika.accounts.length,
				});
			}
			if (this.checkPermission('Koop') && (this.investmentsData.koop.accounts.length || this.$store.state.views.koopStatus.hasContract)){
				const koopStatus = this.$store.state.views.koopStatus;
				const needsConsent = !koopStatus.consent && koopStatus.hasContract;
				const showConsentPrompt = needsConsent && !koopStatus.isRelation;
				const showStatusText = needsConsent && koopStatus.isRelation;

				boxes.push({
					name: `Kooperativa DSS`,
					total: this.investmentsData.koop.total.toFixed(2),
					deposits: this.investmentsData.koop.deposits.toFixed(2),
					withdrawals: this.investmentsData.koop.withdrawals.toFixed(2),
					profit: this.investmentsData.koop.profit.toFixed(2),
					profitPercentage: this.investmentsData.koop.profit_percentage.toFixed(2),
					fees: this.investmentsData.koop.fees.toFixed(2),
					hideStats: needsConsent || !koopStatus.consent,
					hideValue: needsConsent,
					detailUrl: showConsentPrompt ? this.$t('routes.profile.path') : this.$t('routes.investments_koop.path'),
					redirectDetail: koopStatus.consent ? this.investmentsData.koop.accounts.length : false,
					redirect: showConsentPrompt ? this.$t('routes.profile.path') : false,
					redirectText: showConsentPrompt ? this.$t('investments.koop_grant_consent') : null,
					redirectSwal: showConsentPrompt ? {
						title: this.$t('investments.koop_consent_swal_title'),
						text: this.$t('investments.koop_consent_swal_text'),
						confirmText: this.$t('investments.koop_consent_swal_confirm'),
						cancelText: this.$t('forms.cancel'),
					} : null,
					statusText: showStatusText ? this.$t('investments.koop_no_consent') : null,
				});
			}
			if (this.checkPermission('Gutmann') && this.investmentsData.gutmann.accounts.length){
				boxes.push({
					name: `Bank Gutmann`,
					total: this.investmentsData.gutmann.total.toFixed(2),
					deposits: this.investmentsData.gutmann.deposits.toFixed(2),
					withdrawals: this.investmentsData.gutmann.withdrawals.toFixed(2),
					profit: this.investmentsData.gutmann.profit.toFixed(2),
					profitPercentage: this.investmentsData.gutmann.profit_percentage.toFixed(2),
					fees: this.investmentsData.gutmann.fees.toFixed(2),
					hideStats: this.investmentsData.gutmann.hide_stats,
					detailUrl: this.$t('routes.investments_gutmann.path'),
					redirectDetail: this.investmentsData.gutmann.accounts.length,
				});
			}
			if (this.checkPermission('Sympatia') && this.investmentsData.sympatia.accounts.length){
				boxes.push({
					name: `Sympatia`,
					total: this.investmentsData.sympatia.total.toFixed(2),
					deposits: this.investmentsData.sympatia.deposits.toFixed(2),
					withdrawals: this.investmentsData.sympatia.withdrawals.toFixed(2),
					profit: this.investmentsData.sympatia.profit.toFixed(2),
					profitPercentage: this.investmentsData.sympatia.profit_percentage.toFixed(2),
					fees: this.investmentsData.sympatia.fees.toFixed(2),
					hideStats: this.investmentsData.sympatia.hide_stats,
					detailUrl: this.$t('routes.investments_sympatia.path'),
					redirectDetail: this.investmentsData.sympatia.accounts.length,
				});
			}

			return boxes;
		}
	},
	methods: {
		checkPermission(type){
			let app = process.env.VUE_APP_TYPE;
			let allowedInvestments = this.$store.state.app.allowedInvestments[app];

			return allowedInvestments.includes(type);
		}
	},
	mounted() {

	}
}
</script>

<style scoped lang="scss">
.investments-options-form {
	max-width: 600px;
	margin-bottom: 15px;
}
</style>